import React from 'react';
import { KeyAccountCardData } from '../api';
import MetricStat from './MetricStat';
import Sparkline from './Sparkline';
import DeltaIndicator, { revenueDeltaText, cmDeltaText, snapshotDeltaText } from './DeltaIndicator';
import { formatCompactCurrency, formatPercent, formatDays } from './format';

interface Props {
    account: KeyAccountCardData;
    onClick: (customerCode: string, customerName: string) => void;
}

// every tile after the first gets a vertical divider when the row fits on one line
const dividerClass = 'xl:border-l xl:border-gray-200 xl:pl-4';

const AccountCard: React.FC<Props> = ({ account, onClick }) => (
    <div
        className="bg-white border border-gray-200 rounded-lg shadow-md shadow-gray-200/60 hover:shadow-lg hover:border-gray-300 transition-all cursor-pointer flex flex-col"
        onClick={() => onClick(account.customer_code, account.customer_name)}
    >
        <div className="px-5 pt-4 flex items-start justify-between gap-4">
            <h3 className="text-base font-bold text-gray-900 uppercase leading-snug truncate">
                {account.customer_name}
            </h3>
            <Sparkline data={account.sparkline} className="shrink-0" />
        </div>

        <div className="px-5 py-4 grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-x-4 gap-y-4">
            <MetricStat
                label="Revenue (PY)"
                value={formatCompactCurrency(account.revenue_py)}
            />
            <div className={dividerClass}>
                <MetricStat
                    label="Revenue (CY)"
                    value={formatCompactCurrency(account.revenue_cy)}
                    delta={
                        <DeltaIndicator
                            delta={account.revenue_growth_amt}
                            text={revenueDeltaText(account.revenue_growth_amt, account.revenue_growth_pct)}
                        />
                    }
                />
            </div>
            <div className={dividerClass}>
                <MetricStat
                    label="CM"
                    value={formatCompactCurrency(account.cm_cy)}
                    delta={
                        <DeltaIndicator
                            delta={account.cm_growth_amt}
                            text={revenueDeltaText(account.cm_growth_amt, account.cm_growth_pct)}
                        />
                    }
                />
            </div>
            <div className={dividerClass}>
                <MetricStat
                    label="CM %"
                    value={formatPercent(account.cm_pct_cy)}
                    delta={
                        <DeltaIndicator
                            delta={account.cm_pct_delta}
                            text={account.cm_pct_delta !== null ? cmDeltaText(account.cm_pct_delta) : ''}
                        />
                    }
                />
            </div>
            <div className={dividerClass}>
                <MetricStat
                    label="Overdue >30"
                    value={formatCompactCurrency(account.overdue_gt_30)}
                    valueClassName={account.overdue_gt_30 > 0 ? 'text-red-700' : ''}
                    delta={
                        <DeltaIndicator
                            delta={account.overdue_gt_30_delta}
                            text={account.overdue_gt_30_delta !== null ? snapshotDeltaText(account.overdue_gt_30_delta) : ''}
                            invert
                            nullText="— vs last run"
                        />
                    }
                />
            </div>
            <div className={dividerClass}>
                <MetricStat
                    label="Unbilled excl. stock"
                    value={formatCompactCurrency(account.unbilled_excl_stock)}
                    delta={
                        <DeltaIndicator
                            delta={account.unbilled_excl_stock_delta}
                            text={account.unbilled_excl_stock_delta !== null ? snapshotDeltaText(account.unbilled_excl_stock_delta) : ''}
                            invert
                            nullText="— vs last run"
                        />
                    }
                />
            </div>
            <div className={dividerClass}>
                <MetricStat label="DSO (Days)" value={formatDays(account.dso)} />
            </div>
            <div className={dividerClass}>
                <MetricStat
                    label="Total Exposure"
                    value={formatCompactCurrency(account.total_exposure)}
                />
            </div>
        </div>
    </div>
);

export default AccountCard;
